import React, { createContext, useContext, useState, useCallback } from 'react';
import supabase from '../../../config/supabaseClient';
import toast from 'react-hot-toast';

const ControleDemandaContext = createContext();

export const useControleDemanda = () => {
    const context = useContext(ControleDemandaContext);
    if (!context) {
        throw new Error('useControleDemanda deve ser usado dentro de ControleDemandaProvider');
    }
    return context;
};

export default function ControleDemandaProvider({ children }) {
    const [demandas, setDemandas] = useState([]);
    const [analises, setAnalises] = useState([]);
    const [setores, setSetores] = useState([]);
    const [colaboradores, setColaboradores] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [statusDemandas, setStatusDemandas] = useState([]);
    const [statusAnalises, setStatusAnalises] = useState([]);
    const [loading, setLoading] = useState(false);

    // Carregar demandas com filtros por status
    const carregarDemandas = useCallback(async (statusFiltro = null) => {
        setLoading(true);
        try {
            console.log('Iniciando carregamento de demandas...');

            let query = supabase
                .from('view_demandas_detalhada')
                .select('*')
                .order('created_at', { ascending: false });

            if (statusFiltro) {
                query = query.eq('status_id', statusFiltro);
            }

            console.log('Executando query de demandas...');
            const { data, error } = await query;

            if (error) {
                console.error('Erro na query de demandas:', error);
                throw error;
            }

            console.log('Demandas carregadas:', data?.length || 0, 'registros');
            setDemandas(data || []);
        } catch (error) {
            console.error('Erro ao carregar demandas:', error);
            toast.error('Erro ao carregar demandas: ' + error.message);
        } finally {
            setLoading(false);
        }
    }, []);

    // Carregar análises com filtros por status
    const carregarAnalises = useCallback(async (statusFiltro = null) => {
        setLoading(true);
        try {
            console.log('Iniciando carregamento de análises...');

            let query = supabase
                .from('vw_analise_demanda')
                .select('*')
                .order('analisedemanda_criado_em', { ascending: false });

            if (statusFiltro) {
                query = query.eq('analisedemanda_status_id', statusFiltro);
            }

            console.log('Executando query de análises...');
            const { data, error } = await query;

            if (error) {
                console.error('Erro na query de análises:', error);
                throw error;
            }

            console.log('Análises carregadas:', data?.length || 0, 'registros');
            setAnalises(data || []);
        } catch (error) {
            console.error('Erro ao carregar análises:', error);
            toast.error('Erro ao carregar análises: ' + error.message);
        } finally {
            setLoading(false);
        }
    }, []);

    // Carregar setores
    const carregarSetores = useCallback(async () => {
        try {
            const { data, error } = await supabase
                .from('funcionariosetor')
                .select('*')
                .order('descricao');

            if (error) throw error;
            setSetores(data || []);
        } catch (error) {
            console.error('Erro ao carregar setores:', error);
            toast.error('Erro ao carregar setores');
        }
    }, []);

    // Carregar colaboradores por setor
    const carregarColaboradoresPorSetor = useCallback(async (setorId) => {
        try {
            const { data, error } = await supabase
                .from('funcionariousuario')
                .select('*')
                .eq('setor', setorId)
                .eq('ativo', true)
                .order('nome');

            if (error) throw error;
            return data || [];
        } catch (error) {
            console.error('Erro ao carregar colaboradores:', error);
            toast.error('Erro ao carregar colaboradores');
            return [];
        }
    }, []);

    // Buscar ID numérico do funcionário pelo UUID do Supabase
    const buscarFuncionarioId = useCallback(async (userUid) => {
        try {
            console.log('🔍 Buscando funcionário para UID:', userUid);
            
            const { data, error } = await supabase
                .from('funcionariousuario')
                .select('id')
                .eq('uid', userUid)
                .single();

            if (error) {
                console.error('❌ Erro ao buscar funcionário:', error);
                console.error('❌ UID buscado:', userUid);
                return null;
            }

            console.log('✅ Funcionário encontrado:', data);
            console.log('✅ ID do funcionário:', data?.id);
            
            return data?.id;
        } catch (error) {
            console.error('❌ Erro ao buscar funcionário (catch):', error);
            console.error('❌ UID buscado:', userUid);
            return null;
        }
    }, []);

    // Carregar dados auxiliares
    const carregarDadosAuxiliares = useCallback(async () => {
        try {
            const [categoriasRes, statusDemRes, statusAnalRes] = await Promise.all([
                supabase.from('categoria').select('*').order('nome'),
                supabase.from('demandastatus').select('*').order('nome'),
                supabase.from('analisestatus').select('*').order('descricao')
            ]);

            if (categoriasRes.error) throw categoriasRes.error;
            if (statusDemRes.error) throw statusDemRes.error;
            if (statusAnalRes.error) throw statusAnalRes.error;

            setCategorias(categoriasRes.data || []);
            setStatusDemandas(statusDemRes.data || []);
            setStatusAnalises(statusAnalRes.data || []);
        } catch (error) {
            console.error('Erro ao carregar dados auxiliares:', error);
            toast.error('Erro ao carregar dados auxiliares');
        }
    }, []);  // Responder demanda
    const responderDemanda = useCallback(async (demandaId, resposta, funcionarioUid) => {
        try {
            // Buscar ID numérico do funcionário
            const funcionarioId = await buscarFuncionarioId(funcionarioUid);
            if (!funcionarioId) {
                throw new Error('Funcionário não encontrado');
            }

            const { error } = await supabase
                .from('demanda')
                .update({
                    resposta,
                    funcionarioResposta: funcionarioId,
                    dataResposta: new Date().toISOString(),
                    dataultimaatualizacao: new Date().toISOString()
                })
                .eq('id', demandaId);

            if (error) throw error;

            // Registrar no histórico
            await registrarHistoricoDemanda(demandaId, null, funcionarioId);

            toast.success('Resposta enviada com sucesso!');
            await carregarDemandas();
        } catch (error) {
            console.error('Erro ao responder demanda:', error);
            toast.error('Erro ao responder demanda: ' + error.message);
        }
    }, [carregarDemandas, buscarFuncionarioId]);

    // Encaminhar demanda
    const encaminharDemanda = useCallback(async (demandaId, setorId, colaboradorId, funcionarioUid) => {
        try {
            // Buscar ID numérico do funcionário
            const funcionarioId = await buscarFuncionarioId(funcionarioUid);
            if (!funcionarioId) {
                throw new Error('Funcionário não encontrado');
            }

            // Status "Encaminhada" = ID 3
            const statusEncaminhada = 3;

            const { error } = await supabase
                .from('demanda')
                .update({
                    status: statusEncaminhada,
                    funcionarioencaminhamento: colaboradorId,
                    setorEncaminhamento: setorId,
                    funcionario_alteracao: funcionarioId,
                    dataultimaatualizacao: new Date().toISOString()
                })
                .eq('id', demandaId);

            if (error) throw error;

            // Registrar no histórico
            await registrarHistoricoDemanda(demandaId, statusEncaminhada, funcionarioId, setorId, colaboradorId);

            toast.success('Demanda encaminhada com sucesso!');
            await carregarDemandas();
        } catch (error) {
            console.error('Erro ao encaminhar demanda:', error);
            toast.error('Erro ao encaminhar demanda: ' + error.message);
        }
    }, [carregarDemandas, buscarFuncionarioId]);

    // Concluir demanda
    const concluirDemanda = useCallback(async (demandaId, funcionarioUid) => {
        try {
            // Buscar ID numérico do funcionário
            const funcionarioId = await buscarFuncionarioId(funcionarioUid);
            if (!funcionarioId) {
                throw new Error('Funcionário não encontrado');
            }

            // Status "Concluída" = ID 2
            const statusConcluida = 2;

            const { error } = await supabase
                .from('demanda')
                .update({
                    status: statusConcluida,
                    funcionario_alteracao: funcionarioId,
                    dataultimaatualizacao: new Date().toISOString()
                })
                .eq('id', demandaId);

            if (error) throw error;

            // Registrar no histórico
            await registrarHistoricoDemanda(demandaId, statusConcluida, funcionarioId);

            toast.success('Demanda concluída com sucesso!');
            await carregarDemandas();
        } catch (error) {
            console.error('Erro ao concluir demanda:', error);
            toast.error('Erro ao concluir demanda: ' + error.message);
        }
    }, [carregarDemandas, buscarFuncionarioId]);

    // Finalizar análise
    const finalizarAnalise = useCallback(async (analiseId, funcionarioUid) => {
        try {
            // Buscar ID numérico do funcionário
            const funcionarioId = await buscarFuncionarioId(funcionarioUid);
            if (!funcionarioId) {
                throw new Error('Funcionário não encontrado');
            }

            // Status "Finalizada" = ID 4
            const statusFinalizada = 4;

            const { error } = await supabase
                .from('analisedemanda')
                .update({
                    status: statusFinalizada,
                    alterado_por: funcionarioId,
                    alterado_em: new Date().toISOString()
                })
                .eq('id', analiseId);

            if (error) throw error;

            // Buscar o setor atual da análise para o histórico
            const { data: analiseData, error: setorError } = await supabase
                .from('analisedemanda')
                .select('setor')
                .eq('id', analiseId)
                .single();
            
            if (setorError) {
                console.error('❌ Erro ao buscar setor da análise:', setorError);
            }
            
            const setorAtual = analiseData?.setor;
            console.log('🏢 Setor da análise (finalizar):', setorAtual);
            
            // Validar se o setor é válido
            if (!setorAtual || setorAtual === undefined) {
                console.warn('⚠️ Setor não encontrado na finalização, usando setor padrão');
                // Buscar um setor padrão ou usar o primeiro setor disponível
                const { data: setorPadrao } = await supabase
                    .from('funcionariosetor')
                    .select('id')
                    .limit(1)
                    .single();
                
                const setorFinal = setorPadrao?.id || 1; // Fallback para ID 1
                
                // Registrar no histórico de análise
                await registrarHistoricoAnalise(analiseId, statusFinalizada, funcionarioId, setorFinal);
            } else {
                // Registrar no histórico de análise
                await registrarHistoricoAnalise(analiseId, statusFinalizada, funcionarioId, setorAtual);
            }

            toast.success('Análise finalizada com sucesso!');
            await carregarAnalises();
        } catch (error) {
            console.error('Erro ao finalizar análise:', error);
            toast.error('Erro ao finalizar análise: ' + error.message);
        }
    }, [carregarAnalises, buscarFuncionarioId]);

    // Devolver análise
    const devolverAnalise = useCallback(async (analiseId, descricaoRetorno, funcionarioUid) => {
        try {
            console.log('🔄 Iniciando devolução de análise:', { analiseId, descricaoRetorno, funcionarioUid });
            
            // Buscar ID numérico do funcionário
            console.log('🔍 Buscando funcionário ID para:', funcionarioUid);
            const funcionarioId = await buscarFuncionarioId(funcionarioUid);
            console.log('✅ Funcionário ID encontrado:', funcionarioId);
            
            if (!funcionarioId) {
                throw new Error('Funcionário não encontrado');
            }

            // Status "Devolvida" = ID 2
            const statusDevolvida = 2;
            console.log('📝 Atualizando análise no banco:', { analiseId, statusDevolvida, funcionarioId });

            const { error } = await supabase
                .from('analisedemanda')
                .update({
                    status: statusDevolvida,
                    descricao_retorno: descricaoRetorno,
                    alterado_por: funcionarioId,
                    alterado_em: new Date().toISOString()
                })
                .eq('id', analiseId);

            if (error) {
                console.error('❌ Erro na atualização da análise:', error);
                throw error;
            }
            
            console.log('✅ Análise atualizada com sucesso, registrando histórico...');

            // Buscar o setor atual da análise para o histórico
            const { data: analiseData, error: setorError } = await supabase
                .from('analisedemanda')
                .select('setor')
                .eq('id', analiseId)
                .single();
            
            if (setorError) {
                console.error('❌ Erro ao buscar setor da análise:', setorError);
            }
            
            const setorAtual = analiseData?.setor;
            console.log('🏢 Setor atual da análise:', setorAtual);
            console.log('🏢 Dados da análise:', analiseData);
            
            // Validar se o setor é válido
            if (!setorAtual || setorAtual === undefined) {
                console.warn('⚠️ Setor não encontrado, usando setor padrão');
                // Buscar um setor padrão ou usar o primeiro setor disponível
                const { data: setorPadrao } = await supabase
                    .from('funcionariosetor')
                    .select('id')
                    .limit(1)
                    .single();
                
                const setorFinal = setorPadrao?.id || 1; // Fallback para ID 1
                console.log('🏢 Usando setor fallback:', setorFinal);
                
                // Registrar no histórico de análise
                await registrarHistoricoAnalise(analiseId, statusDevolvida, funcionarioId, setorFinal);
            } else {
                // Registrar no histórico de análise
                await registrarHistoricoAnalise(analiseId, statusDevolvida, funcionarioId, setorAtual);
            }
            
            console.log('✅ Histórico registrado, finalizando...');

            toast.success('Análise devolvida com sucesso!');
            await carregarAnalises();
        } catch (error) {
            console.error('❌ Erro ao devolver análise:', error);
            console.error('❌ Tipo do erro:', typeof error);
            console.error('❌ Erro stringificado:', JSON.stringify(error, null, 2));
            console.error('❌ Detalhes do erro:', {
                message: error?.message,
                code: error?.code,
                details: error?.details,
                hint: error?.hint,
                stack: error?.stack
            });
            toast.error('Erro ao devolver análise: ' + (error?.message || 'Erro desconhecido'));
        }
    }, [carregarAnalises, buscarFuncionarioId]);

    // Transferir análise
    const transferirAnalise = useCallback(async (analiseId, novoSetorId, novoColaboradorId, funcionarioUid) => {
        try {
            // Buscar ID numérico do funcionário
            const funcionarioId = await buscarFuncionarioId(funcionarioUid);
            if (!funcionarioId) {
                throw new Error('Funcionário não encontrado');
            }

            // Status "Transferida" = ID 3
            const statusTransferida = 3;

            const { error } = await supabase
                .from('analisedemanda')
                .update({
                    status: statusTransferida,
                    transferido_para_setor: novoSetorId,
                    funcionario_envolvido: novoColaboradorId,
                    setor: novoSetorId,
                    alterado_por: funcionarioId,
                    alterado_em: new Date().toISOString()
                })
                .eq('id', analiseId);

            if (error) throw error;

            // Registrar no histórico de análise
            await registrarHistoricoAnalise(analiseId, statusTransferida, funcionarioId, novoSetorId);

            toast.success('Análise transferida com sucesso!');
            await carregarAnalises();
        } catch (error) {
            console.error('Erro ao transferir análise:', error);
            toast.error('Erro ao transferir análise: ' + error.message);
        }
    }, [carregarAnalises, buscarFuncionarioId]);

    // Registrar histórico de demanda
    const registrarHistoricoDemanda = useCallback(async (demandaId, statusId, funcionarioId, setorId = null, funcionarioResponsavel = null) => {
        try {
            const { error } = await supabase
                .from('demandahistorico')
                .insert({
                    demanda: demandaId,
                    status: statusId,
                    funcionario: funcionarioId,
                    setor: setorId,
                    funcionario_responsavel: funcionarioResponsavel
                });

            if (error) throw error;
        } catch (error) {
            console.error('Erro ao registrar histórico de demanda:', error);
        }
    }, []);

    // Registrar histórico de análise
    const registrarHistoricoAnalise = useCallback(async (analiseId, statusId, funcionarioId, setorId = null) => {
        try {
            console.log('📝 Registrando histórico de análise - ENTRADA:', { 
                analiseId, 
                statusId, 
                funcionarioId, 
                setorId,
                tipos: {
                    analiseId: typeof analiseId,
                    statusId: typeof statusId,
                    funcionarioId: typeof funcionarioId,
                    setorId: typeof setorId
                }
            });
            
            // Garantir valores padrão para evitar undefined
            const analiseIdSafe = analiseId || 0;
            const statusIdSafe = statusId || 1;
            const funcionarioIdSafe = funcionarioId || 1;
            const setorIdSafe = setorId || 1;
            
            console.log('📝 Valores com fallback:', { 
                analiseIdSafe, 
                statusIdSafe, 
                funcionarioIdSafe, 
                setorIdSafe 
            });
            
            // Converter para números para garantir tipo correto
            const analiseIdNum = parseInt(analiseIdSafe);
            const statusIdNum = parseInt(statusIdSafe);
            const funcionarioIdNum = parseInt(funcionarioIdSafe);
            const setorIdNum = parseInt(setorIdSafe);
            
            console.log('📝 Valores convertidos:', { 
                analiseIdNum, 
                statusIdNum, 
                funcionarioIdNum, 
                setorIdNum,
                isNaN: {
                    analiseIdNum: isNaN(analiseIdNum),
                    statusIdNum: isNaN(statusIdNum),
                    funcionarioIdNum: isNaN(funcionarioIdNum),
                    setorIdNum: isNaN(setorIdNum)
                }
            });
            
            // Verificar se algum valor é NaN
            if (isNaN(analiseIdNum) || isNaN(statusIdNum) || isNaN(funcionarioIdNum) || isNaN(setorIdNum)) {
                console.error('❌ Valores NaN detectados:', {
                    analiseIdNum: isNaN(analiseIdNum) ? 'NaN' : analiseIdNum,
                    statusIdNum: isNaN(statusIdNum) ? 'NaN' : statusIdNum,
                    funcionarioIdNum: isNaN(funcionarioIdNum) ? 'NaN' : funcionarioIdNum,
                    setorIdNum: isNaN(setorIdNum) ? 'NaN' : setorIdNum
                });
                throw new Error('Valores inválidos detectados - algum campo resultou em NaN');
            }
            
            const insertData = {
                analise: analiseIdNum,
                status: statusIdNum,
                funcionario_envolvido: funcionarioIdNum,
                setor_envolvido: setorIdNum,
                funcionario: funcionarioIdNum
            };
            
            console.log('📝 Dados finais para inserção:', insertData);
            
            const { error } = await supabase
                .from('analisehistorico')
                .insert(insertData);

            if (error) {
                console.error('❌ Erro no insert do histórico:', error);
                console.error('❌ Detalhes do erro do histórico:', {
                    message: error.message,
                    code: error.code,
                    details: error.details,
                    hint: error.hint
                });
                throw error;
            }
            
            console.log('✅ Histórico de análise registrado com sucesso');
        } catch (error) {
            console.error('❌ Erro ao registrar histórico de análise:', error);
            throw error; // Re-throw para que o erro seja capturado pela função pai
        }
    }, []);

    // Buscar histórico de demanda
    const buscarHistoricoDemanda = useCallback(async (demandaId) => {
        try {
            const { data, error } = await supabase
                .from('demandahistorico')
                .select(`
          *,
          status_info:status(nome, descricao),
          funcionario_info:funcionario(nome, email),
          setor_info:setor(descricao),
          funcionario_resp_info:funcionario_responsavel(nome, email)
        `)
                .eq('demanda', demandaId)
                .order('criado_em', { ascending: false });

            if (error) throw error;
            return data || [];
        } catch (error) {
            console.error('Erro ao buscar histórico de demanda:', error);
            return [];
        }
    }, []);

    // Buscar análise específica usando a view
    const buscarAnaliseDetalhada = useCallback(async (analiseId) => {
        try {
            console.log('Buscando análise detalhada:', analiseId);
            
            const { data, error } = await supabase
                .from('vw_analise_demanda')
                .select('*')
                .eq('analisedemanda_id', analiseId)
                .single();

            if (error) {
                console.error('Erro ao buscar análise detalhada:', error);
                throw error;
            }

            console.log('Análise detalhada carregada:', data);
            return data;
        } catch (error) {
            console.error('Erro ao buscar análise detalhada:', error);
            throw error;
        }
    }, []);

    // Buscar histórico de análise
    const buscarHistoricoAnalise = useCallback(async (analiseId) => {
        try {
            const { data, error } = await supabase
                .from('analisehistorico')
                .select(`
          *,
          status_info:status(descricao),
          funcionario_envolvido_info:funcionario_envolvido(nome, email),
          setor_envolvido_info:setor_envolvido(descricao),
          funcionario_info:funcionario(nome, email)
        `)
                .eq('analise', analiseId)
                .order('criado_em', { ascending: false });

            if (error) throw error;
            return data || [];
        } catch (error) {
            console.error('Erro ao buscar histórico de análise:', error);
            return [];
        }
    }, []);

    const value = {
        // Estados
        demandas,
        analises,
        setores,
        colaboradores,
        categorias,
        statusDemandas,
        statusAnalises,
        loading,

        // Funções de carregamento
        carregarDemandas,
        carregarAnalises,
        carregarSetores,
        carregarColaboradoresPorSetor,
        carregarDadosAuxiliares,

        // Ações de demanda
        responderDemanda,
        encaminharDemanda,
        concluirDemanda,

        // Ações de análise
        finalizarAnalise,
        devolverAnalise,
        transferirAnalise,

        // Histórico
        buscarHistoricoDemanda,
        buscarHistoricoAnalise,
        
        // Busca específica
        buscarAnaliseDetalhada,
        
        // Utilitários
        buscarFuncionarioId
    };

    return (
        <ControleDemandaContext.Provider value={value}>
            {children}
        </ControleDemandaContext.Provider>
    );
}